import { useCallback, useEffect } from 'react';
import { useFeatureFlagVariantKey, usePostHog } from 'posthog-js/react';

import { useDialog } from '../hooks/useDialog';
import { useStore } from '../hooks/useStore';
import useTranslation from '../hooks/useTranslation';
import useCurrentCurrency from '../hooks/useCurrentCurrency';

import { formatPrice } from '@/lib/utils/currency';

import SamsungTradeIn from '../components/icons/SamsungTradeIn';
import { useNavigator } from '../hooks/useNavigator';
import { Button } from '@/lib/components/ui/button';
import tradeInImage from '@/public/assets/samsungTradeIn.jpg';
import Devices from './icons/Devices';
import { CheckIcon } from 'lucide-react';

const StartBanner = () => {
  const posthog = usePostHog();
  const bannerTextVariant = useFeatureFlagVariantKey('banner-text');
  const translations = useTranslation();
  const setShowDialog = useDialog((state) => state.setShowDialog);
  const currency = useCurrentCurrency();
  const campaign = useStore((state) => state.campaign);
  const bannerText = useStore((state) => state.bannerText);

  const onClick = useCallback(() => {
    setShowDialog(true);
  }, [setShowDialog]);

  useEffect(() => {
    posthog.capture('Show Start Banner', { bannerText });
  }, [posthog, bannerText]);

  return (
    <section className="@container/banner text-left overflow-hidden font-samsungOneRegular text-black banner">
      <div className="flex">
        <div className="flex flex-col grow justify-between p-4 @[484px]/banner:p-6">
          <div className="flex items-center gap-2 mb-2">
            <SamsungTradeIn className="w-5 h-5 shrink-0 animate-logo-flip" />
            <p className="font-samsungSharpSans text-lg @[484px]/banner:text-xl @[484px]/banner:leading-6">
              {translations.bannerTitle}
            </p>
          </div>
          <p className="text-base mb-4">{translations.bannerSubTitle}</p>
          {Boolean(campaign?.amount) && (
            <p className="text-sm mb-6 p-2 rounded bg-secondaryBackground text-samsungBlue text-center">
              {`${translations.bannerBonusTitle}`}{' '}
              <strong className="font-samsungOneBold">
                {formatPrice(campaign?.amount, currency)}
              </strong>{' '}
              {translations.bannerBonusDesc}
            </p>
          )}
          {Boolean(bannerText && bannerTextVariant === 'test') && (
            <p className="text-sm mb-6 p-2 rounded bg-secondaryBackground font-samsungOneBold text-samsungBlue text-center">
              {bannerText}
            </p>
          )}
          <Button
            variant="outline"
            aria-label={translations.bannerButton}
            onClick={onClick}
            className="self-start"
          >
            {translations.bannerButton}
          </Button>
        </div>
        <img
          src={tradeInImage}
          alt=""
          className="hidden @[480px]/banner:block @[550px]/banner:w-60 w-45 h-auto object-cover"
        />
      </div>
    </section>
  );
};

const CompletedBanner = () => {
  const posthog = usePostHog();
  const translations = useTranslation();
  const tradeIn = useStore((state) => state.tradeIn);
  const setShowDialog = useDialog((state) => state.setShowDialog);
  const resetNavigation = useNavigator((state) => state.reset);
  const small = useStore((state) => state.small);
  const partner = useStore((state) => state.partner);

  const onClick = useCallback(() => {
    resetNavigation();
    setShowDialog(true);
  }, [setShowDialog, resetNavigation]);

  if (!tradeIn) return null;

  useEffect(() => {
    posthog.capture('Show Completed Banner', {
      amount: tradeIn.amount,
      currency: tradeIn.currency,
      small: small,
    });
  }, []);

  return (
    <section className="@container/banner text-left overflow-hidden font-samsungOneRegular text-black banner">
      <div className="flex flex-col gap-4 p-4 @[484px]/banner:p-6 @[450px]/banner:flex-row">
        <div className="flex flex-col grow justify-between grow gap-2">
          <div className="flex w-full gap-2 mb-2">
            <CheckIcon
              size="20"
              color="white"
              className="bg-samsungBlue rounded-full p-1"
            />
            <p className="flex flex-row w-full font-samsungSharpSans text-lg @[484px]/banner:text-xl @[484px]/banner:leading-6 justify-between">
              {translations.completeTitle}
              <span
                className="text-sm font-normal underline align-middle cursor-pointer whitespace-nowrap"
                role="button"
                onClick={onClick}
              >
                {translations['completedRedo']}
              </span>
            </p>
          </div>
          <p className="text-xl">{tradeIn.model?.ProductName}</p>
          {!small && (
            <p className="text-base">{translations.completedDescription}</p>
          )}
        </div>
        <div className="flex flex-col items-center justify-center self-center shrink text-center gap-2">
          {!small && (
            <>
              {tradeIn.model?.PictureUrl ? (
                <img
                  alt=""
                  className="object-contain max-w-32 max-h-30"
                  src={tradeIn.model?.PictureUrl}
                />
              ) : (
                <Devices className="object-contain max-w-28 max-h-20 scale-125" />
              )}
            </>
          )}
          <div>
            <p className="text-2xl font-samsungOneBold">
              {formatPrice(tradeIn.amount, tradeIn.currency)}
            </p>
            <p className="text-xs italic">{translations.priceGrading}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

const Banner = () => {
  const tradeIn = useStore((state) => state.tradeIn);

  return tradeIn ? <CompletedBanner /> : <StartBanner />;
};

export default Banner;
